/**
 * @file 发送 Page View
 * @author dafo<huanghouin@baijiahulian.com>
 * @module module/view
 */

import request from './util/request';
import configurable from './util/configurable';
import {camelToUnder} from './util/json';
import {
    on,
    off,
    getPageURL
} from './util/dom';
import {
    trackID,
    getUserID,
    Config
} from './util/config';
import warnForbiddenKeys from './util/forbiddenWords';
import metric from './metric';
import onRouteChange from './util/onRouteChange';
import getPath from './util/getPath';
import {detectStorage} from './util/detectStorageAvailability';

const CAN_I_USE_STORE = detectStorage('sessionStorage');

/**
 * 上一个页面的 URL，对于 SPA 来说是上一个路由的地址
 */
let previousPageURL;

/**
 * 在 sessionStorage 中保存上一页地址的键名
 *
 * @const {string}
 */
const STORAGE_KEY = 'pre_page_str';

/**
 * 保存上一页地址到 sessiongStorage
 *
 * @function
 * @param {string=} [url=previousPageURL] 要保存作为上一个页面地址的 URL
 */
const save = (url = previousPageURL) => {
    if (!url) {
        url = getPageURL();
    }

    CAN_I_USE_STORE && sessionStorage.setItem(STORAGE_KEY, url);
};

/**
 * 页面浏览上报
 *
 * @function
 * @param {Object} json 要上报的数据
 * @param {Function=} callback 上报后的回调
 */
const report = configurable((json, callback) => {

    const {
        user_number = getUserID(),
        user_id = user_number,
        event_id = '',
        page_type,
        page_str,
        pre_page_str = (CAN_I_USE_STORE ? sessionStorage.getItem(STORAGE_KEY) : ''),
        ...rest
    } = camelToUnder(json);

    // 必要的上报数据
    const pageViewData = {
        // 当前页面 URL
        page_url: location.href,

        // 事件 id
        event_id,

        // 前一个页面的 URL
        referrer: document.referrer,

        // 同 user_id，兼容之用
        user_number,

        // 登录用户的 number
        user_id,

        // 页面类型，可以直接写死为业务，例如高途为 gaotu
        page_type,

        // 页面具体类别划分，例如：/gaotu/detail/course@a=1@b=2
        page_str,

        // 前一个页面的 page_str
        pre_page_str,

        // 用户的唯一标识，由后端生成写到 cookie 中
        track_id: trackID,

        // 其他自定义的数据
        ...rest
    };

    if (page_str) {
        previousPageURL = page_str;
        save(page_str);
    }

    warnForbiddenKeys(pageViewData);

    // 自动上报点击指标数据到轻舟
    metric({
        ...pageViewData,
        metric: 'view'
    });
    request(Config.VERB)(`${Config.PV}`, pageViewData, callback);
});

const win = typeof window !== 'undefined' && window;

/**
 * 在 unload 时保存当前页面作为上一个页面的地址
 *
 * @function
 */
const savePreviousPageURL = () => {
    off(win, 'unload', savePreviousPageURL);
    save();
};

on(win, 'unload', savePreviousPageURL);


report.initAutoPV = (conf = {}) => {
    // 自动上报 metric 为 pv 的指标日志
    const autoPV = e => {
        const {isTrusted, habo} = e;
        if (isTrusted || (habo && !isTrusted)) {
            const user_number = getUserID();

            metric({
                // 当前页面 URL
                page_url: location.href,

                // 前一个页面的 URL
                referrer: document.referrer,

                // 同 user_id，兼容之用
                // TODO user_id 用来计算uv，放在日志的哪一层待定
                user_number,

                // 登录用户的 number
                user_id: user_number,

                // 前一个页面的 page_str
                pre_page_str: previousPageURL,

                // 用户的唯一标识，由后端生成写到 cookie 中
                track_id: trackID,

                // 页面pathname
                path: getPath(),

                ...conf,

                // 其他自定义的数据
                metric: 'pv'
            });
        }


    };
    const teardown = onRouteChange(autoPV);
    on(win, 'unload', () => {
        teardown();
    });
    on(win, 'load', autoPV);
};

export default report;
